import {
    ChangeDetectionStrategy,
    Component,
    forwardRef,
    Injector,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
} from '@angular/core';
import {
    ControlValueAccessor,
    FormBuilder,
    FormControl,
    FormGroup,
    NgControl,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
} from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, takeUntil, filter, tap } from 'rxjs/operators';
import { wrapYear } from '../../../mappers/date';
import { MONTHS } from '../../../config/months';

interface ContractStartMonths {
    date: string;
    label: string;
}

@Component({
    selector: 'app-contract-start-picker',
    imports: [ReactiveFormsModule],
    templateUrl: './contract-start-picker.component.html',
    styleUrl: './contract-start-picker.component.scss',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ContractStartPickerComponent),
            multi: true,
        },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContractStartPickerComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {
    @Input()
    public monthsInAdvance = 3;
    @Input()
    public mode: 'day' | 'month' = 'day';
    @Input()
    public labelledById: string;

    public startDateForm: FormGroup;
    public months: ContractStartMonths[] = [];
    public days: string[] = [];
    private onChange?: (val: string | null) => void;
    private onTouched?: () => void;
    private unsubscribe$: Subject<void> = new Subject<void>();
    private control: FormControl;
    private controlSubscription: Subscription;
    public constructor(
        private formBuilder: FormBuilder,
        private injector: Injector,
    ) {
        this.initForm();
        this.subscribeToValueChanges();
    }

    public ngOnInit(): void {
        this.setMonthAndYearOptions();
        this.setDayOptions();
    }

    public ngOnChanges(): void {
        setTimeout(() => {
            const ngControl = this.injector.get(NgControl, null);
            try {
                this.control = ngControl.control as FormControl;
                if (this.controlSubscription) {
                    this.controlSubscription.unsubscribe();
                }
                this.controlSubscription = this.subscribeToValidationChanges();
            } catch {
                throw new Error('birthday picker is missing form control binding');
            }
        });
        this.setMonthAndYearOptions();
        this.setDayOptions();
    }

    public ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    public writeValue(value: string | null): void {
        if (typeof value === 'string' && /^[0-9]{2}.[0-9]{2}.[0-9]{4}$/.test(value)) {
            const i = value.indexOf('.');
            if (i !== -1) {
                this.startDateForm.patchValue({
                    day: value.slice(0, i),
                    month: value.slice(i + 1),
                });
            }
        } else if (value === null) {
            this.startDateForm.patchValue({ day: null, month: null });
        }
    }

    public registerOnChange(fn: (val: string | null) => void): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    private initForm(): void {
        this.startDateForm = this.formBuilder.group({
            day: [null],
            month: [null],
        });
    }

    private subscribeToValueChanges(): void {
        this.startDateForm.valueChanges
            .pipe(
                distinctUntilChanged(),
                tap(() => {
                    if (typeof this.onTouched === 'function') {
                        this.onTouched();
                    }
                }),
                filter((val) => this.checkForInvalidDays(val)),
                takeUntil(this.unsubscribe$),
            )
            .subscribe((val) => {
                if (val.day !== null && val.month !== null && typeof this.onChange === 'function') {
                    this.onChange(`${('00' + val.day).slice(-2)}.${val.month}`);
                }
            });
    }

    private setMonthAndYearOptions(): void {
        const months = MONTHS.split(' ');
        const today = new Date();
        this.months = [];
        const start = today.getMonth() + (this.mode === 'month' && today.getDate() > 14 ? 1 : 0);
        for (let i = start; i < start + this.monthsInAdvance; i++) {
            const m = i % 12;
            this.months.push({
                date: `${('00' + (m + 1)).slice(-2)}.${wrapYear(i, today.getFullYear())}`,
                label: `${this.mode === 'month' ? '1. ' : ''} ${months[m]} ${wrapYear(i, today.getFullYear())}`,
            });
        }
    }

    private setDayOptions(): void {
        if (this.mode === 'month') {
            this.days = ['01'];
        } else {
            this.days = [];
            for (let i = 1; i < 32; i++) {
                this.days.push(('00' + i).slice(-2));
            }
        }
    }

    private checkForInvalidDays(date: { day: null | string; month: null | string }): boolean {
        if (date.day === null || date.month === null) {
            return false;
        }
        if (this.mode === 'month') {
            return true;
        }
        const month = date.month.slice(0, 2);
        const year = date.month.slice(3);
        if ('01;03;05;07;08;10;12'.split(';').indexOf(month) !== -1) {
            this.setNumberOfDays(31);
        } else {
            if (month === '02') {
                // leap year?
                if ((Number(year) % 4 === 0 && Number(year) % 100 !== 0) || Number(year) % 400 === 0) {
                    this.setNumberOfDays(29);
                } else {
                    this.setNumberOfDays(28);
                }
            } else {
                this.setNumberOfDays(30);
            }
        }
        if (this.days.indexOf(date.day) === -1) {
            this.startDateForm.get('day').setValue(this.days[this.days.length - 1]);
            return false;
        }
        return true;
    }

    private setNumberOfDays(numberOfDays: number): void {
        this.days.splice(numberOfDays, 4);
        while (this.days.length < numberOfDays) {
            this.days.push(this.days.length + 1 + '');
        }
    }

    private subscribeToValidationChanges(): Subscription {
        return this.control.statusChanges.subscribe((status) => {
            if (status === 'INVALID') {
                const errors = { validation_failed: true };
                this.startDateForm.get('month').setErrors(errors);
                this.startDateForm.get('day').setErrors(errors);
            } else {
                this.startDateForm.get('month').setErrors(null);
                this.startDateForm.get('day').setErrors(null);
            }
            if (this.control.touched) {
                this.startDateForm.markAllAsTouched();
            }
        });
    }
}
